import { getRequestAuthPayload } from '@helpers/getRequestAuth';
import type { GetCsvUrlResponse } from '@responses/csv';
import type { GetUsersResponse } from '@responses/users';
import { UserCapabilityManager } from '@root/helpers/user-management';
import type { CanonicalUserRecordWithCapabilities } from '@root/helpers/user-management/types';
import { createUser, deleteUser, updateUser } from '@src/mutations';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import type { QueryFactory } from './factory.types';

export const fetchUsers = async (): Promise<CanonicalUserRecordWithCapabilities[]> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: '/users',
  });

  const response = await axios<GetUsersResponse>(networkPayload);

  const users = Array.isArray(response.data) ? response.data : [];

  return users.map((user) => ({
    ...user,
    capabilitiesManager: new UserCapabilityManager(user),
  }));
};

export const userQueries = {
  list: () =>
    queryOptions({
      queryKey: ['users'] as const,
      queryFn: fetchUsers,
    }),
} satisfies QueryFactory;

export function useUsers() {
  return useQuery(userQueries.list());
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof createUser>[0]) => createUser(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userQueries.list().queryKey }),
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ username, payload }: { username: string; payload: Parameters<typeof updateUser>[1] }) =>
      updateUser(username, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userQueries.list().queryKey }),
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (username: string) => deleteUser(username),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userQueries.list().queryKey }),
  });
}

export const fetchUsersCSVDownloadUrl = async (): Promise<string> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: '/users',
    params: { format: 'csv' },
  });

  const response = await axios<GetCsvUrlResponse>(networkPayload);
  return response.data.downloadUrl;
};
